
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const CookiePolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 hover:bg-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToHome')}
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white p-8 rounded-t-2xl">
            <h1 className="text-4xl font-bold mb-4">{t('legal.cookiePolicy.title')}</h1>
            <p className="text-lg opacity-90">
              {t('legal.cookiePolicy.lastUpdated')}
            </p>
          </div>

          <div className="bg-white p-8 rounded-b-2xl shadow-lg">
            <div className="prose max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.whatAreCookies.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.cookiePolicy.whatAreCookies.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.howWeUseCookies.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.cookiePolicy.howWeUseCookies.description')}
                </p>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.howWeUseCookies.essential.title')}
                    </h3>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600">
                      {(t('legal.cookiePolicy.howWeUseCookies.essential.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.howWeUseCookies.performance.title')}
                    </h3>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600">
                      {(t('legal.cookiePolicy.howWeUseCookies.performance.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.howWeUseCookies.functional.title')}
                    </h3>
                    <ul className="list-disc pl-6 space-y-1 text-gray-600">
                      {(t('legal.cookiePolicy.howWeUseCookies.functional.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.typesOfCookies.title')}
                </h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.typesOfCookies.session.title')}
                    </h3>
                    <p className="text-gray-600">
                      {t('legal.cookiePolicy.typesOfCookies.session.content')}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.typesOfCookies.persistent.title')}
                    </h3>
                    <p className="text-gray-600">
                      {t('legal.cookiePolicy.typesOfCookies.persistent.content')}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.typesOfCookies.firstParty.title')}
                    </h3>
                    <p className="text-gray-600">
                      {t('legal.cookiePolicy.typesOfCookies.firstParty.content')}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('legal.cookiePolicy.typesOfCookies.thirdParty.title')}
                    </h3>
                    <p className="text-gray-600">
                      {t('legal.cookiePolicy.typesOfCookies.thirdParty.content')}
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.managingCookies.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.cookiePolicy.managingCookies.description')}
                </p>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {t('legal.cookiePolicy.managingCookies.browserSettings.title')}
                  </h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-600">
                    {(t('legal.cookiePolicy.managingCookies.browserSettings.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.thirdPartyServices.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('legal.cookiePolicy.thirdPartyServices.description')}
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-600">
                  {(t('legal.cookiePolicy.thirdPartyServices.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.updatesToPolicy.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.cookiePolicy.updatesToPolicy.content')}
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {t('legal.cookiePolicy.contactUs.title')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('legal.cookiePolicy.contactUs.content')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
